// Pure per-tick decision logic for the scan loop, pulled out of the rAF closure in
// useScanLoop.ts so it's unit-testable without a real camera or canvas — and so the loop itself
// stays a thin dispatcher instead of a single branchy function. Ported from event-connect's
// scanner (frame-tick.ts) unchanged.

import type { Rect, Size } from './decoder/types'
import { viewfinderFrameRect } from './viewfinder'

const HAVE_CURRENT_DATA = 2

export interface VideoFrameSize {
  readonly width: number
  readonly height: number
  readonly readyState: number
}

export interface FrameTickInput {
  readonly now: number
  readonly lastDetectAt: number
  readonly intervalMs: number
  readonly pending: boolean
  readonly video: VideoFrameSize | null
  /** The <video>'s on-screen box, for cropping the decode to the viewfinder. Null/zero
   * before layout settles — the decode then falls back to the whole frame. */
  readonly display: Size | null
}

export type FrameTickDecision =
  | { readonly kind: 'skip' }
  | { readonly kind: 'detect'; readonly now: number; readonly frame: Rect }

/** Whether this rAF tick should crop a frame and run a decode, throttled to ~intervalMs. */
export function decideFrameTick(input: FrameTickInput): FrameTickDecision {
  if (input.pending) return { kind: 'skip' }
  if (input.now - input.lastDetectAt < input.intervalMs) return { kind: 'skip' }
  if (!input.video || input.video.readyState < HAVE_CURRENT_DATA) return { kind: 'skip' }
  if (input.video.width === 0 || input.video.height === 0) return { kind: 'skip' }
  const frameSize = { width: input.video.width, height: input.video.height }
  return { kind: 'detect', now: input.now, frame: decodeRect(frameSize, input.display) }
}

/** The viewfinder ROI when the display box is known, else the whole frame (so the decoder
 * is never starved before layout, and a degenerate crop can't zero out the decode). */
function decodeRect(frameSize: Size, display: Size | null): Rect {
  const fullFrame = { x: 0, y: 0, ...frameSize }
  if (!display || display.width === 0 || display.height === 0) return fullFrame
  const roi = viewfinderFrameRect(frameSize, display)
  return roi.width > 0 && roi.height > 0 ? roi : fullFrame
}

export interface FrameCaptureResult {
  readonly ok: boolean
  readonly resized: boolean
}

/**
 * Resizes the offscreen canvas to the frame (if needed) and does the 1:1 9-arg drawImage crop
 * (dw/dh always equal the source crop's size, never downscaled, or roiToFrame's translation
 * silently stops being valid).
 */
export function captureFrame(video: CanvasImageSource, canvas: HTMLCanvasElement, frame: Rect): FrameCaptureResult {
  const resized = canvas.width !== frame.width || canvas.height !== frame.height
  if (resized) {
    canvas.width = frame.width
    canvas.height = frame.height
  }
  const context = canvas.getContext('2d')
  if (!context) return { ok: false, resized }
  context.drawImage(video, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height)
  return { ok: true, resized }
}
